import React from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, Clock, Share2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import CM50GlobalImage from "../../../assets/images/harsh-cm-50.jpg";

const CM50GlobalPost = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center justify-between mb-8">
          <motion.button
            onClick={() => navigate("/articles/blog")}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center text-red-600 hover:text-red-700 group"
          >
            <ArrowLeft className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" />
            Back to Blog
          </motion.button>
          <motion.button
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => navigator.share?.({ title: document.title, url: window.location.href })}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <Share2 className="w-4 h-4" />
            Share
          </motion.button>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <span className="px-4 py-1.5 bg-red-100 text-red-600 rounded-full text-sm font-medium">
              Global Leadership
            </span>
            <div className="flex items-center text-gray-500 text-sm">
              <Calendar className="w-4 h-4 mr-1.5" />
              November 2025
            </div>
            <div className="flex items-center text-gray-500 text-sm">
              <Clock className="w-4 h-4 mr-1.5" />
              8 min read
            </div>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Young Indian Cooperator Harsh Shines at CM50 Global Conference
          </h1>

          <p className="text-xl md:text-2xl text-gray-600 mb-8 leading-relaxed">
            Global Leaders Adopt 'New Global Economy' Framework at CM50
          </p>

          <div className="mb-12 -mx-4 sm:mx-0">
            <img
              src={CM50GlobalImage}
              alt="Harsh Sanghani at CM50 Global Conference in Doha"
              className="w-full rounded-none sm:rounded-xl shadow-2xl"
            />
          </div>

          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              I had the honour of representing the voice of global youth at the CM50 – Cooperative Ministers Conference held in Doha, where I participated in my capacity as the <strong>President of the International Cooperative Alliance (ICA) Youth Committee</strong> and <strong>Youth Representative to the ICA Global Board</strong>. This was a landmark gathering of global cooperative leadership, marking the 50th anniversary of the ICA's engagement with governments and policymakers. The conference brought together cooperative ministers, heads of multilateral organisations, and global leaders from <strong>over 100 countries</strong>.
            </p>

            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              Being introduced at the event as the nephew of <strong>Shri Dileep Sanghani</strong>, Chairman of IFFCO and NCUI, was a moment of pride for my family and for the cooperative movement in India.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
              A Historic Summit for Global Cooperation
            </h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              The CM50 summit served as a significant high-level policy dialogue on how cooperatives can shape the future of the world economy. One of the most historic outcomes of the event was the collective adoption of <strong>"A Contract for a New Global Economy"</strong> — a transformative framework that places <strong>people, planet, and peace before profit</strong>.
            </p>

            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              This framework projects <strong>cooperatives as the "Third Way of Economy"</strong> — offering a balanced and humane alternative to both capitalism and socialism. It envisions an economic model that promotes growth with <strong>equity, sustainability, and social well-being</strong>.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
              Representing Youth on the Global Stage
            </h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              At the conference, I was the <strong>only young leader</strong> among ministers and policymakers from across the world. My participation ensured that the perspectives, aspirations, and priorities of young people and future generations were <strong>actively integrated</strong> into the global cooperative dialogue.
            </p>

            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              I emphasized that youth must <strong>not just inherit the future — but shape it</strong> by participating in policymaking and leadership within the cooperative movement.
            </p>

            <div className="bg-gradient-to-br from-gray-50 to-gray-100 border-l-4 border-gray-400 rounded-r-lg p-8 my-12">
              <p className="text-gray-800 text-xl leading-relaxed italic">
                "Seeing our vision for cooperatives as the Third Way of Economy being recognised and accepted internationally is truly remarkable."
              </p>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
              Gratitude and Vision
            </h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              Reflecting on the experience, it was an <strong>honour</strong> to represent young cooperators worldwide at such a pivotal global platform. I expressed my <strong>deep gratitude to Shri Mukundraoji Deobhankar</strong>, whose mentorship has guided my efforts toward strengthening the global cooperative vision.
            </p>

            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              I highlighted that the adoption of <em>"A Contract for a New Global Economy"</em> is <strong>more than a policy declaration</strong> — it is a <strong>global commitment</strong> to building economies rooted in <strong>democracy, dignity, and sustainability</strong>. I called for <strong>greater youth involvement</strong> in the cooperative movement, emphasizing that the future vitality of the sector depends on <strong>fresh energy, inclusivity, and innovation</strong>.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
              A Shared Resolve for the Future
            </h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              The CM50 conference concluded with a shared resolve among global leaders to <strong>advance cooperative-led solutions</strong> to global challenges such as inequality, climate change, and social exclusion — reaffirming cooperatives as a <strong>bridge between economic development and human welfare</strong>.
            </p>

            <p className="text-lg text-gray-500 mt-12 mb-8">- Harsh M. Sanghani</p>

            <div className="mt-8 pt-8 border-t border-gray-200">
              <button
                onClick={() => navigate("/articles/blog")}
                className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                <ArrowLeft className="w-5 h-5" />
                Read More Articles
              </button>
            </div>
          </div>
        </motion.div>
      </article>
    </div>
  );
};

export default CM50GlobalPost;
